import { AddIcon, Button, Grid, GridRow, GroupedGroupOption } from '@bp/ui-components';
import { FieldArray, useFormikContext } from 'formik';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useUserConfigContext } from '../../../../hooks/useUserConfigContext';
import { LessonFormClassType, LessonFormType, VersionLessonFormType } from '../../types';
import { LessonClassesFormItem } from './LessonClassesFormItem';

type LessonClassesFormProps = { disabled: boolean; filter?: (c: { uuid: string; grade?: number | null }) => boolean };

export const LessonClassesForm = memo(({ disabled, filter }: LessonClassesFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;

  const { values, setFieldTouched } = useFormikContext<LessonFormType | VersionLessonFormType>();

  const usedClassesUuids = values.lessonClasses?.map((lc) => lc.class.uuid);

  const { classesData, groupsData, divisionsData } = useLoadBasicData({ pause: !usedClassesUuids });

  const classOptions =
    classesData?.classes
      .filter((c) => c.schoolYear.uuid === currentSchoolYear?.uuid)
      .filter((c) => {
        if (filter) {
          return filter({ grade: c.grade, uuid: c.uuid });
        }
        return true;
      })
      .map((c) => {
        return { value: c.uuid, label: c.name, disabled: usedClassesUuids?.includes(c.uuid) };
      }) ?? [];

  return (
    <FieldArray name={'lessonClasses'}>
      {(arrayHelpers) => {
        return (
          <Grid useFormGap>
            {values.lessonClasses?.map((lessonClass, index) => {
              const currentClass = classesData?.classes.find((c) => c.uuid === lessonClass.class.uuid);
              const classDivisionsUuids = currentClass?.divisions.map((d) => d.uuid);

              const groupOptions: GroupedGroupOption[] =
                divisionsData?.divisions
                  .filter((d) => classDivisionsUuids?.includes(d.uuid))
                  .map((d) => {
                    const groupUuids = d.groupsConnection.edges.map((g) => g.node.uuid);
                    const groups = groupsData?.groups.filter((g) => groupUuids.includes(g.uuid));
                    return {
                      label: d.name,
                      options:
                        groups?.map((g) => {
                          return { value: g.uuid, label: g.name };
                        }) ?? [],
                    };
                  }) ?? [];

              return (
                <LessonClassesFormItem
                  key={lessonClass.uuid}
                  disabled={disabled}
                  classOptions={classOptions}
                  classUuid={currentClass?.uuid ?? ''}
                  className={currentClass?.name ?? ''}
                  groupOptions={groupOptions}
                  groups={lessonClass.groups.map((g) => g.uuid)}
                  index={index}
                  onClassChange={async (classUuid) => {
                    arrayHelpers.replace(index, {
                      groups: [],
                      class: { uuid: classUuid },
                      uuid: lessonClass.uuid,
                    });
                  }}
                  onGroupsChange={async (groups) => {
                    arrayHelpers.replace(index, {
                      groups: groups.map((uuid) => ({ uuid })),
                      class: { uuid: lessonClass.class.uuid },
                      uuid: lessonClass.uuid,
                    });
                  }}
                  onDelete={() => arrayHelpers.remove(index)}
                />
              );
            })}

            <GridRow spacingTop='none' spacingBottom='none'>
              <Button
                hierarchy='tertiary'
                disabled={disabled}
                icon={<AddIcon />}
                onClick={async () => {
                  if (values.lessonClasses && values.lessonClasses.length > 0) {
                    for (const index in values.lessonClasses) {
                      await setFieldTouched(`lessonClasses.${index}`);
                    }
                  }
                  const lessonClass: LessonFormClassType = {
                    uuid: v4(),
                    class: { uuid: v4() },
                    groups: [],
                  };
                  arrayHelpers.push(lessonClass);
                }}
              >
                {t('classes.createClass')}
              </Button>
            </GridRow>
          </Grid>
        );
      }}
    </FieldArray>
  );
});

LessonClassesForm.displayName = 'LessonClassesForm';
